import {
  type ChatMessages,
  createGenerateSignedUrlRequest,
  useAssetClient,
  useCallOptions,
  withBearerToken,
} from "@storytell/cp-client";
import { StButton } from "@storytell/ui/buttons";
import {
  TbChevronDown,
  TbChevronRight,
  TbFileText,
  TbLoader2,
  TbMapPin,
  TbPhoto,
  TbSearch,
  TbTable,
} from "solid-icons/tb";
import { type Component, For, Show, createSignal } from "solid-js";
import { extractMarkdownText } from "~/utils/textUtils";
import { useWire } from "~/wire";

interface SearchResultsRendererProps {
  searchResults: ChatMessages.SearchResult;
}

interface AssetSummaryItemProps {
  asset: ChatMessages.AssetSummary | ChatMessages.AssetSearchResultItem;
}

const AssetSummaryItem = (props: AssetSummaryItemProps) => {
  const [isDownloading, setIsDownloading] = createSignal(false);
  const wire = useWire();

  const assetId = () => ("id" in props.asset ? props.asset.id : props.asset.assetId);
  const assetType = () => ("type" in props.asset ? props.asset.type : props.asset.assetType);
  const filename = () => ("originalFilename" in props.asset ? props.asset.originalFilename : props.asset.filename);
  const formatFileSize = (bytes: bigint) => {
    const units = ["B", "KB", "MB", "GB"];
    let size = Number(bytes);
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }

    return `${size.toFixed(1)} ${units[unitIndex]}`;
  };

  const downloadAsset = async (assetId: string, filename: string) => {
    if (isDownloading()) return; // Prevent double-clicks

    setIsDownloading(true);

    try {
      const client = useAssetClient();
      const request = createGenerateSignedUrlRequest({
        assetId,
      });
      const authToken = wire.auth.token();

      if (!authToken) {
        throw new Error("No authentication token available");
      }

      const callOptions = useCallOptions(withBearerToken(authToken));

      // Get the signed URL
      const response = await client.generateSignedURL(request, callOptions);

      if (response.isOk()) {
        const signedUrl = response.value.signedUrl;

        // Create a temporary link element to trigger download
        const link = document.createElement("a");
        link.href = signedUrl;
        link.download = filename || "download";
        link.target = "_blank";

        // Append to body, click, and remove
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        console.error("Failed to get signed URL:", response.error);
        throw new Error("Failed to get signed URL");
      }
    } catch (error) {
      console.error("Failed to download asset:", error);
      // TODO: Show error toast/notification to user
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div class="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
      <div class="flex items-start gap-3">
        <div class="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
          <Show
            when={assetType() === "image"}
            fallback={<TbFileText class="w-4 h-4 text-blue-600 dark:text-blue-400" />}
          >
            <TbPhoto class="w-4 h-4 text-blue-600 dark:text-blue-400" />
          </Show>
        </div>

        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2 mb-1">
            <h4 class="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
              {props.asset.title || filename()}
            </h4>
            <span class="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded">
              {assetType()}
            </span>
          </div>

          <Show when={filename() && filename() !== props.asset.title}>
            <p class="text-xs text-gray-500 dark:text-gray-400 mb-1 truncate">{filename()}</p>
          </Show>

          <Show when={props.asset.summary}>
            <p class="text-sm text-gray-700 dark:text-gray-300 mb-2 line-clamp-2">{props.asset.summary}</p>
          </Show>

          <div class="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
            <span>{formatFileSize(props.asset.sizeBytes)}</span>
            <Show when={assetId()}>
              <StButton
                noBorder
                noColor
                class="text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => downloadAsset(assetId(), filename())}
                loading={isDownloading()}
              >
                {isDownloading() ? "Downloading..." : "Download"}
              </StButton>
            </Show>
          </div>
        </div>
      </div>
    </div>
  );
};

interface ChunkCardProps {
  chunk: ChatMessages.ChunkResult;
  asset: ChatMessages.AssetSearchResultItem;
  index: number;
  searchQuery: string;
}

const ChunkCard: Component<ChunkCardProps> = (props) => {
  const [isExpanded, setIsExpanded] = createSignal(false);

  const truncateContent = (content: string, maxChars: number) => {
    if (content.length <= maxChars) return content;
    return `${content.substring(0, maxChars)}...`;
  };

  const relevanceLabel = () => {
    const score = props.chunk.score;
    // Score is a distance metric: lower = more relevant
    if (score < 0.71) return { label: "High", color: "text-green-600 dark:text-green-400" };
    if (score < 0.76) return { label: "Medium", color: "text-yellow-600 dark:text-yellow-400" };
    return { label: "Low", color: "text-gray-600 dark:text-gray-400" };
  };

  const getAssetTypeIcon = () => {
    const assetType = props.asset.assetType?.toLowerCase() || "";

    if (assetType.includes("image") || assetType.includes("photo") || assetType.includes("picture")) {
      return <TbPhoto class="w-5 h-5 text-blue-600 dark:text-blue-400" />;
    }

    if (assetType.includes("spreadsheet") || assetType.includes("excel") || assetType.includes("csv")) {
      return <TbTable class="w-5 h-5 text-blue-600 dark:text-blue-400" />;
    }

    return <TbFileText class="w-5 h-5 text-blue-600 dark:text-blue-400" />;
  };

  return (
    <div class="group border border-gray-200 dark:border-gray-700 rounded-lg p-4">
      <div class="flex items-start gap-3">
        <div class="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
          {getAssetTypeIcon()}
        </div>

        <div class="flex-1 min-w-0">
          <div class="flex items-center justify-between gap-2 mb-2">
            <div class="flex items-center gap-2 flex-1 min-w-0">
              <h4 class="font-semibold text-sm text-gray-900 dark:text-gray-100 truncate">
                {props.asset.title || props.asset.filename}
              </h4>
            </div>
            <div class="flex items-center gap-2 flex-shrink-0">
              <span class="text-xs text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded-full">
                Citation {props.chunk.position}
              </span>
              <span class="text-xs font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded">
                #{props.index + 1}
              </span>
            </div>
          </div>

          <div class="mb-3 relative">
            <div
              class="absolute left-0 top-0 bottom-0 w-1 rounded-l transition-colors"
              classList={{
                "bg-green-500": props.chunk.score < 0.71,
                "bg-yellow-500": props.chunk.score >= 0.71 && props.chunk.score < 0.76,
                "bg-gray-400": props.chunk.score >= 0.76,
              }}
            />
            <div class="bg-gray-50 dark:bg-gray-900/30 rounded-md p-3 pl-4 border border-gray-200 dark:border-gray-800">
              <p class="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                {extractMarkdownText(isExpanded() ? props.chunk.content : truncateContent(props.chunk.content, 350))}
              </p>
              <Show when={props.chunk.content.length > 350}>
                <button
                  type="button"
                  onClick={() => setIsExpanded(!isExpanded())}
                  class="text-xs text-blue-600 dark:text-blue-400 hover:underline mt-2 flex items-center gap-1"
                >
                  {isExpanded() ? "Show less" : "Show more"}
                  <TbChevronDown class="size-3 transition-transform" classList={{ "rotate-180": isExpanded() }} />
                </button>
              </Show>
            </div>
          </div>

          <div class="flex items-center gap-4 text-xs text-gray-600 dark:text-gray-400">
            <Show when={props.chunk.score !== undefined}>
              <div class="flex items-center gap-1" title={`Relevance score: ${props.chunk.score.toFixed(3)}`}>
                <span>Relevance:</span>
                <span class={relevanceLabel().color}>{relevanceLabel().label}</span>
              </div>
            </Show>
            <Show when={props.chunk.location?.pageNumber}>
              <div class="flex items-center gap-1">
                <TbMapPin class="w-3.5 h-3.5" />
                <span>Page {props.chunk.location?.pageNumber}</span>
              </div>
            </Show>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Renders search results in a structured format instead of markdown
 */
export const SearchResultsRenderer = (props: SearchResultsRendererProps) => {
  const duration = () => {
    const duration = props.searchResults.duration;
    if (!duration) return "0 seconds";

    let totalSeconds: number;
    if (duration.seconds !== undefined) {
      totalSeconds = Number(duration.seconds) + Number(duration.nanos || 0) / 1000000000;
    } else {
      const durationNum = Number(duration);
      totalSeconds = !Number.isNaN(durationNum) ? durationNum : 0;
    }
    // Round to nearest 0.5
    const rounded = Math.round(totalSeconds * 2) / 2;
    const label = rounded === 1 ? "second" : "seconds";
    return `${rounded} ${label}`;
  };

  const [showAssets, setShowAssets] = createSignal(false);
  const [showChunks, setShowChunks] = createSignal(true);
  const [showAllChunks, setShowAllChunks] = createSignal(false);

  // Get all chunks from all assets for display
  const allChunks = () => {
    // Handle both oneOf discriminated union structure and flattened fallback
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    const assets = props.searchResults.results?.value?.assets || (props.searchResults as any).assets?.assets || [];
    const chunks: Array<{ chunk: ChatMessages.ChunkResult; asset: ChatMessages.AssetSearchResultItem }> = [];

    for (const asset of assets) {
      for (const chunk of asset.chunks || []) {
        chunks.push({ chunk, asset });
      }
    }

    // Sort by relevance score (lowest first - lower score = more relevant)
    return chunks.sort((a, b) => a.chunk.score - b.chunk.score);
  };

  const displayedChunks = () => {
    const chunks = allChunks();
    return showAllChunks() ? chunks : chunks.slice(0, 3);
  };

  const totalChunkCount = () => {
    // Handle both oneOf discriminated union structure and flattened fallback
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    const assets = props.searchResults.results?.value?.assets || (props.searchResults as any).assets?.assets || [];
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    return assets.reduce((sum: any, asset: { chunks: string | any[] }) => sum + (asset.chunks?.length || 0), 0);
  };

  return (
    <div>
      <div class="flex items-center gap-1 mb-2">
        <TbSearch class="size-4 text-muted-text" />
        <h3 class="text-sm text-secondary-text">
          Searched for <span class="font-medium text-primary-text">"{props.searchResults.query}"</span>
        </h3>
      </div>

      <Show
        when={
          (props.searchResults.results?.case === "assets" && props.searchResults.results.value?.assets?.length > 0) ||
          // biome-ignore lint/suspicious/noExplicitAny: <explanation>
          (props.searchResults as any).assets?.assets?.length > 0
        }
        fallback={<p class="text-sm text-primary-text italic">No search results found</p>}
      >
        {/* Chunks Section */}
        <Show when={allChunks().length > 0}>
          <button
            type="button"
            onClick={() => setShowChunks((prev) => !prev)}
            class="group/btn flex items-center gap-2 mb-2 cursor-pointer hover:translate-x-1 transition-transform duration-200"
          >
            <p class="text-sm text-blue-800 dark:text-blue-400 font-medium group-hover/btn:underline">
              Found {totalChunkCount()} relevant citation{totalChunkCount() !== 1 ? "s" : ""} in {duration()}
            </p>

            <TbChevronRight
              class="size-3.5 stroke-3 text-secondary-text transition-transform"
              classList={{ "rotate-90": showChunks() }}
            />
          </button>

          <Show when={showChunks()}>
            <div class="space-y-3 mb-4">
              <For each={displayedChunks()}>
                {(item, index) => (
                  <ChunkCard
                    chunk={item.chunk}
                    asset={item.asset}
                    index={index()}
                    searchQuery={props.searchResults.query}
                  />
                )}
              </For>

              <Show when={allChunks().length > 3}>
                <div class="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
                  <p class="text-xs text-gray-600 dark:text-gray-400">
                    Showing {showAllChunks() ? "all" : "top 3 of"} {totalChunkCount()} citation
                    {totalChunkCount() !== 1 ? "s" : ""}
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowAllChunks((prev) => !prev)}
                    class="text-xs text-blue-600 dark:text-blue-400 hover:underline font-medium"
                  >
                    {showAllChunks() ? "Show less" : "Show all"}
                  </button>
                </div>
              </Show>
            </div>
          </Show>
        </Show>

        {/* Assets Section */}
        <button
          type="button"
          onClick={() => setShowAssets((prev) => !prev)}
          class="group/btn flex items-center gap-2 mb-2 cursor-pointer"
        >
          <p class="text-sm text-blue-800 dark:text-blue-400 font-medium group-hover/btn:underline">
            {props.searchResults.results?.value?.assets?.length} source asset
            {props.searchResults.results?.value?.assets?.length !== 1 ? "s" : ""}
          </p>

          <TbChevronRight
            class="size-3.5 stroke-3 text-secondary-text transition-transform"
            classList={{ "rotate-90": showAssets() }}
          />
        </button>

        <Show when={showAssets()}>
          <div class="space-y-3">
            {/* biome-ignore lint/suspicious/noExplicitAny: <explanation> */}
            <For each={props.searchResults.results?.value?.assets || (props.searchResults as any).assets?.assets || []}>
              {(asset) => <AssetSummaryItem asset={asset} />}
            </For>
          </div>
        </Show>
      </Show>
    </div>
  );
};
